import React, { useCallback, useState } from 'react';
import {
  AppButton,
  Box,
  Inscription,
  LinkIcon,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { useHistory } from '@/modules/core/hooks';
import { useGetRenewals } from '@/modules/Renewals/hooks';
import { useAllowedRenewals } from '@/modules/Renewals/hooks';
import { Renewal, RenewalStatus } from '@/modules/Renewals/renewals.types';
import RenewalSetupModal from '@/modules/Renewals/RenewalSetup/RenewalSetupModal/RenewalSetupModal.tsx';

const getBannerTexts = (renewal?: Renewal): {
  title: string;
  description: string;
} => {
  switch (renewal?.status) {
    case RenewalStatus.NEW:
      return {
        title: 'Renew your plan for the upcoming plan year',
        description: `Your current plan year, ${renewal?.planYear?.name}, ends on ${dayjs(renewal?.planYear?.validTo).format(DEFAULT_DATE_FORMAT)}. Renew your plans now to ensure there are no gaps in coverage.`,
      };
    case RenewalStatus.CANCELED:
      return {
        title: 'Your non-renewal request has been sent',
        description: 'You\'ve chosen not to renew your plans. Your reasoning is listed in details to this banner. An account manager will reach out to gather additional details.',
      };
    case RenewalStatus.REQUESTED:
      return {
        title: 'Renewal request sent!',
        description: 'Your request to renew your plan has been sent to your account manager. They will be in touch to confirm the details and complete your plan\'s renewal.',
      };
    default:
      return {
        title: 'Unknown status',
        description: `Status for ${renewal?.planYear} is not recognized.`,
      };
  }
};

const PlansRenewalsBanners: React.FC = () => {
  const [isRenewDetailsModalVisible, setIsRenewDetailsModalVisible] = useState(false);
  const [selectedRenewal, setSelectedRenewal] = useState<Renewal>();

  const { renewals } = useGetRenewals();
  const allowedRenewals = useAllowedRenewals(renewals);
  const history = useHistory();

  const handleViewDetails = useCallback((renewal: Renewal) => {
    setSelectedRenewal(renewal);
    setIsRenewDetailsModalVisible(true);
  }, []);

  const handleGetStartedClick = useCallback((planYearId?: string) => {
    window.open(`${ROUTES.RENEWALS_SETUP_BY_PLAN_YEAR_ID(planYearId)}/${history.location.search}`,
      '_blank'
    );
  }, [history]);

  const getActionButton = useCallback((renewal: Renewal): React.ReactNode => {
    switch (renewal.status) {
      case RenewalStatus.NEW:
        return (
          <AppButton onClick={() => handleGetStartedClick(renewal?.planYearId)} rightIcon={<LinkIcon color="iconAccent" size="small" />}>
            Get Started
          </AppButton>
        );
      case RenewalStatus.CANCELED:
      case RenewalStatus.REQUESTED:
        return (
          <AppButton onClick={() => handleViewDetails(renewal)}>
            View Details
          </AppButton>
        );
      default:
        return null;
    }
  }, [handleGetStartedClick, handleViewDetails]);

  if (!allowedRenewals?.length) return null;

  return (
    <>
      <Box margin={{ bottom: 'l' }} gap="m">
        {
          allowedRenewals?.map((renewal) => {
            const { title, description } = getBannerTexts(renewal);
            const actionButton = getActionButton(renewal);
            return (
              <WarningCard
                key={renewal.id}
                containerProps={{
                  pad: {
                    horizontal: 'l',
                    vertical: 'm',
                  },
                }}
              >
                <Box direction="row" justify="between" align="center" gap="medium">
                  <Box flex={{ grow: 1, shrink: 1 }} basis="auto" overflow="hidden">
                    <Box margin={{ bottom: 'xxs' }} direction="row" align="center">
                      <WarningIcon />
                      <Inscription lineHeight="20px" color="textBody" weight="bold" margin={{ left: 'xs' }}>
                        {title}
                      </Inscription>
                    </Box>
                    <Inscription
                      lineHeight="20px"
                      color="textBody"
                      wordBreak="break-word"
                    >
                      {description}
                    </Inscription>
                  </Box>
                  <Box flex={{ shrink: 0 }}>
                    {actionButton}
                  </Box>
                </Box>
              </WarningCard>
            );
          })
        }
      </Box>
      <RenewalSetupModal
        isVisible={isRenewDetailsModalVisible}
        isViewMode
        renewalSetupData={selectedRenewal}
        onSetVisible={setIsRenewDetailsModalVisible}
      />
    </>

  );
};

export default PlansRenewalsBanners;
